import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';  
import './App.css';
import "bootstrap/dist/css/bootstrap.min.css";
import HomePage from './components/home/HomePage';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import CollectionPage from './components/collections/CollectionPage';
import NewArrivalsPage from './components/newarrivals/NewArrivalsPage';
import { WishlistProvider } from './context/WishlistContext';
import WishlistPage from './components/wishlist/WishlistPage';
import { ToastProvider } from './context/ToastContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AdminPanel from './components/admin/AdminPanel';
import LoginPage from './components/admin/LoginPage';
import ProtectedRoute from './components/admin/ProtectedRoute';
import { AuthProvider } from './context/AuthContext';
import { UserAuthProvider } from './context/UserAuthContext';
import AuthPage from './components/auth/AuthPage';
import DiamondPricePage from './components/diamonds/DiamondPricePage';
import DiamondMatchmaker from './components/diamonds/matchmaker/DiamondMatchmaker';
import SpecialOffersPage from './components/offers/SpecialOffersPage';
import { initializeProducts } from './services/ProductService';
import ProductService from './services/ProductService';
import ScrollToTop from './components/common/ScrollToTop';
import { Link } from 'react-router-dom';

// Import new pages
import AboutUs from './components/pages/AboutUs';
import FAQ from './components/pages/FAQ';
import ContactUs from './components/pages/ContactUs';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import Blog from './components/pages/Blog';

// Custom welcome toast component
const WelcomeMessage = () => (
  <div className="welcome-toast">
    <h4>Welcome to NM Diamond!</h4>
    <p>Try our <strong>Diamond Price Evaluator</strong> by clicking the diamond icon in the navbar.</p>
    <p>NEW: Find your perfect diamond with our <Link to="/diamond-matchmaker" style={{ color: '#3498db', textDecoration: 'underline' }}>AI Diamond Matchmaker</Link>!</p>
    <div className="diamond-hint">
      <span className="pulse-dot"></span>
      <span>Look for the glowing diamond</span>
    </div>
  </div>
);

function App() {
  const [loading, setLoading] = useState(true);
  
  // Show welcome message on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('hasVisitedBefore');
    
    if (!hasVisited) {
      // Wait a bit before showing the welcome message
      setTimeout(() => {
        toast(<WelcomeMessage />, {
          position: "top-center",
          autoClose: 8000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          className: 'welcome-toast-container'
        });
        
        // Set flag in localStorage
        localStorage.setItem('hasVisitedBefore', 'true');
      }, 2000);
    }
  }, []);
  
  // Sync products to database on app startup
  useEffect(() => {
    const syncProducts = async () => {
      try {
        // Initialize products first
        await ProductService.getAllProducts();
        
        // Then sync all products to the database using the dedicated endpoint
        await ProductService.syncAllProductsToDatabase();
        console.log('All products synced to database successfully');
        
        // Add a small delay to ensure database operations complete
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } catch (error) {
        console.error('Error syncing products to database:', error);
        
        // Fallback to regular sync if the dedicated endpoint fails
        try {
          await ProductService.syncProductsToDatabase();
          console.log('Products synced using fallback method');
        } catch (fallbackError) {
          console.error('Fallback sync also failed:', fallbackError);
        } finally {
          setLoading(false);
        }
      }
    };
    
    syncProducts();
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <ToastProvider>
        <AuthProvider>
          <UserAuthProvider>
            <WishlistProvider>
              <div className="app-container">
                <Navbar/>
                <main className="main-content">
                  <Routes>
                    <Route path="/" element={<HomePage/>} />
                    <Route path="/collections" element={<CollectionPage/>} />
                    <Route path="/new-arrivals" element={<NewArrivalsPage/>} />
                    <Route path="/wishlist" element={<WishlistPage/>} />
                    <Route path="/auth" element={<AuthPage/>} />
                    <Route path="/diamonds" element={<DiamondPricePage/>} />
                    {/* Use a more SEO-friendly approach for diamond-evaluator route */}
                    <Route path="/diamond-evaluator" element={<DiamondPricePage/>} />
                    {/* New route for Diamond Matchmaker */}
                    <Route path="/diamond-matchmaker" element={<DiamondMatchmaker/>} />
                    <Route path="/offers" element={<SpecialOffersPage/>} />
                    <Route path="/admin/login" element={<LoginPage/>} />
                    <Route path="/admin" element={
                      <ProtectedRoute>
                        <AdminPanel/>
                      </ProtectedRoute>
                    } />
                    
                    {/* New routes for footer pages */}
                    <Route path="/about" element={<AboutUs/>} />
                    <Route path="/faq" element={<FAQ/>} />
                    <Route path="/contact" element={<ContactUs/>} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy/>} />
                    <Route path="/blog" element={<Blog/>} />
                  </Routes>
                </main>
                <Footer/>
                <ToastContainer 
                  position="top-right"
                  autoClose={3000}
                  hideProgressBar={false}
                  newestOnTop
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="dark"
                />
              </div>
            </WishlistProvider>
          </UserAuthProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export default App;
